import React, { useEffect, useState } from 'react';
import { api, handleApiError } from '../../lib/api';

interface ApiKey {
  id: string;
  key: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  permissions: { routes: string[] };
  user?: { id: string; email: string };
}

interface User {
  id: string;
  email: string;
}

const APIKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [name, setName] = useState('');
  const [availableRoutes, setAvailableRoutes] = useState<string[]>([]);
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [userId, setUserId] = useState('');
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);

  const loadKeys = async () => {
    try {
      const res = await api.admin.apiKeys.list();
      setKeys(res.data?.data || []);
    } catch (err) {
      console.error(handleApiError(err));
    }
  };

  const loadRoutes = async () => {
    try {
      const res = await api.admin.apiKeys.routes();
      setAvailableRoutes(res.data?.data || []);
    } catch (err) {
      console.error(handleApiError(err));
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.users.getAll();
      const fetched = (res.data?.data?.users || []).map((u: any) => ({
        id: u.id,
        email: u.email,
      }));
      setUsers(fetched);
    } catch (err) {
      console.error(handleApiError(err));
    }
  };

  useEffect(() => {
    void loadKeys();
    void loadRoutes();
    void loadUsers();
  }, []);

  const createKey = async () => {
    try {
      const res = await api.admin.apiKeys.create({
        name,
        permissions: selectedRoutes,
        userId,
        expiresAt: expiresAt || undefined,
      });
      setNewSecret(res.data?.data?.secret || null);
      setNewKey(res.data?.data?.key || null);
      setName('');
      setSelectedRoutes([]);
      setExpiresAt('');
      setUserId('');
      void loadKeys();
    } catch (err) {
      console.error(handleApiError(err));
    }
  };

  const toggleRoute = (route: string) => {
    setSelectedRoutes((prev) =>
      prev.includes(route) ? prev.filter((r) => r !== route) : [...prev, route]
    );
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      await api.admin.apiKeys.update(id, { isActive: !isActive });
      void loadKeys();
    } catch (err) {
      console.error(handleApiError(err));
    }
  };

  const regenerate = async (id: string) => {
    try {
      const res = await api.admin.apiKeys.regenerate(id);
      setNewSecret(res.data?.data?.secret || null);
    } catch (err) {
      console.error(handleApiError(err));
    }
  };

  const remove = async (id: string) => {
    try {
      await api.admin.apiKeys.remove(id);
      void loadKeys();
    } catch (err) {
      console.error(handleApiError(err));
    }
  };

  const highlightUnused = (k: ApiKey) => {
    if (!k.lastUsedAt) return true;
    const days = (Date.now() - new Date(k.lastUsedAt).getTime()) / (1000 * 60 * 60 * 24);
    return days > 30;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
        <h2 className="text-white text-lg mb-4">Create API Key</h2>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="flex-1 p-2 rounded bg-gray-800 text-white"
          />
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="flex-1 p-2 rounded bg-gray-800 text-white"
          >
            <option value="">Select User</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
              </option>
            ))}
          </select>
          <div className="flex-1 p-2 rounded bg-gray-800 text-white max-h-40 overflow-y-auto space-y-1">
            {availableRoutes.map((r) => (
              <label key={r} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedRoutes.includes(r)}
                  onChange={() => toggleRoute(r)}
                />
                <span>{r}</span>
              </label>
            ))}
          </div>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="p-2 rounded bg-gray-800 text-white"
          />
          <button
            onClick={createKey}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Create
          </button>
        </div>
        {newKey && (
          <p className="mt-2 text-yellow-400">New key: {newKey}</p>
        )}
        {newSecret && (
          <p className="mt-2 text-yellow-400">New secret: {newSecret}</p>
        )}
      </div>

      <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-800">
            <tr>
              <th className="p-2 text-gray-300">Name</th>
              <th className="p-2 text-gray-300">Key</th>
              <th className="p-2 text-gray-300">User</th>
              <th className="p-2 text-gray-300">Status</th>
              <th className="p-2 text-gray-300">Created</th>
              <th className="p-2 text-gray-300">Last Used</th>
              <th className="p-2 text-gray-300">Permissions</th>
              <th className="p-2 text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {keys.map((k) => (
              <tr key={k.id} className={highlightUnused(k) ? 'bg-red-900/20' : ''}>
                <td className="p-2 text-white">{k.name}</td>
                <td className="p-2 text-gray-300 font-mono break-all">{k.key}</td>
                <td className="p-2 text-gray-300">{k.user?.email || ''}</td>
                <td className="p-2 text-white">{k.isActive ? 'Active' : 'Inactive'}</td>
                <td className="p-2 text-gray-300">{new Date(k.createdAt).toLocaleDateString()}</td>
                <td className="p-2 text-gray-300">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}</td>
                <td className="p-2 text-gray-300">{k.permissions?.routes?.join(', ')}</td>
                <td className="p-2 space-x-2">
                  <button onClick={() => toggleActive(k.id, k.isActive)} className="text-blue-400">Toggle</button>
                  <button onClick={() => regenerate(k.id)} className="text-yellow-400">Regenerate</button>
                  <button onClick={() => remove(k.id)} className="text-red-400">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default APIKeysPage;
